import React, { useState, useRef } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { requester, fileRequester } from 'App';

// PDF.js 워커 설정
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const SignedPdfViewer = () => {
    const [contractId, setContractId] = useState('');
    const [pdfBytes, setPdfBytes] = useState(null);
    const [pageCount, setPageCount] = useState(0);
    const [pdfLoaded, setPdfLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const canvasRefs = useRef([]);

    // PDF 로드 함수
    const loadPdf = async () => {
        if (!contractId.trim()) {
            setErrorMessage('계약서 ID를 입력해주세요.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setPdfLoaded(false);
        canvasRefs.current = [];

        try {
            // 서명된 계약서 PDF 가져오기
            const response = await new Promise((resolve, reject) => {
                fileRequester.getSignedContractPdf(contractId, (data, error) => {
                    if (error || !data) {
                        reject(error || new Error('PDF 데이터를 가져오지 못했습니다.'));
                    } else {
                        resolve(data);
                    }
                });
            });

            // PDF 데이터 처리
            const uint8Array = new Uint8Array(response.slice(0));

            if (uint8Array.length === 0) {
                throw new Error('PDF 데이터가 비어 있습니다.');
            }

            setPdfBytes(uint8Array);

            // PDF 로드 및 렌더링
            const loadingTask = getDocument({
                data: new Uint8Array(uint8Array.buffer.slice(0)),
                // 추가 옵션들...
            });
            const pdf = await loadingTask.promise;
            setPageCount(pdf.numPages);
            setPdfLoaded(true);
        } catch (error) {
            console.error('PDF 로드 오류:', error);
            setErrorMessage(`PDF를 불러오는 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // PDF 페이지 렌더링
    React.useEffect(() => {
        const renderPages = async () => {
            if (!pdfBytes || !pdfLoaded || canvasRefs.current.length === 0) return;

            try {
                const pdfBytesClone = new Uint8Array(pdfBytes.slice(0));
                const pdf = await getDocument({ data: pdfBytesClone }).promise;

                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const viewport = page.getViewport({ scale: 1.5 });

                    const canvas = canvasRefs.current[i - 1]?.current;
                    if (!canvas) continue;

                    const context = canvas.getContext('2d');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await page.render({ canvasContext: context, viewport }).promise;
                }
            } catch (error) {
                console.error('PDF 렌더링 오류:', error);
                setErrorMessage(`PDF 렌더링 중 오류가 발생했습니다: ${error.message}`);
            }
        };

        renderPages();
    }, [pdfBytes, pdfLoaded]);

    // PDF 직접 다운로드 함수
    const downloadPdf = () => {
        if (!pdfBytes) {
            setErrorMessage('다운로드할 PDF가 없습니다.');
            return;
        }

        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `signed-contract-${contractId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '20px',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
                📄 서명된 계약서 조회
            </h2>

            {/* ID 입력 및 조회 버튼 */}
            <div style={{
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    계약서 ID:
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        value={contractId}
                        onChange={(e) => setContractId(e.target.value)}
                        placeholder="계약서 ID를 입력하세요"
                        style={{
                            flex: 1,
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '16px'
                        }}
                    />
                    <button
                        onClick={loadPdf}
                        disabled={isLoading}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: isLoading ? '#cccccc' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            fontSize: '16px'
                        }}
                    >
                        {isLoading ? '로딩 중...' : '조회'}
                    </button>

                    {pdfLoaded && (
                        <button
                            onClick={downloadPdf}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            다운로드
                        </button>
                    )}
                </div>

                {errorMessage && (
                    <div style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        borderRadius: '4px'
                    }}>
                        {errorMessage}
                    </div>
                )}
            </div>

            {/* PDF 미리보기 */}
            {pdfLoaded && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    marginBottom: '30px'
                }}>
                    <h3 style={{ textAlign: 'center', color: '#333' }}>계약서 미리보기</h3>
                    {Array.from({ length: pageCount }, (_, i) => {
                        if (!canvasRefs.current[i]) {
                            canvasRefs.current[i] = React.createRef();
                        }
                        return (
                            <canvas
                                key={i}
                                ref={canvasRefs.current[i]}
                                style={{
                                    border: '1px solid #ccc',
                                    width: '100%',
                                    height: 'auto',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    borderRadius: '4px'
                                }}
                            />
                        );
                    })}
                </div>
            )}

            {/* 로딩 표시 */}
            {isLoading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <p style={{ fontSize: '18px', fontWeight: 'bold' }}>PDF 로딩 중...</p>
                        <p>잠시만 기다려주세요.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignedPdfViewer;